import type {RouteQueryKey} from '@github-ui/react-core/query-key'
import {useMutation, useQueryClient} from '@github-ui/react-query'
import {sanitize} from '@github-ui/sanitize'
import type {CreateRepositoryLabelMutationMutation} from '@github-ui/server-persisted-queries/types/createRepositoryLabelMutation'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'
import {useCallback, useRef} from 'react'

import {LABELS} from './constants/labels'
import type {LabelsQueryData} from './types'
import {formatFormErrorMessage} from './utils/format-form-error-message'
import {updateLabelsCache} from './utils/update-labels-cache'

type UseCreateLabelOptions = {
  repositoryId: string
  repositoryNameWithOwner: string
  queryKey?: RouteQueryKey
  setSubmissionErrors: (v: string) => void
  setDialogOpen: (open: boolean) => void
  onSuccess?: () => void
}

type CreateLabelInput = {
  name: string
  description: string
  color: string
}

type MutationContext = {
  previousData: LabelsQueryData | undefined
  tempId: string
}

export const TEMP_LABEL_ID = 'temp-new-label-'

export function useCreateLabel({
  repositoryId,
  repositoryNameWithOwner,
  queryKey,
  setSubmissionErrors,
  setDialogOpen,
  onSuccess,
}: UseCreateLabelOptions) {
  const queryClient = useQueryClient()
  const setIsSubmittingRef = useRef<(isSubmitting: boolean) => void>(() => {})

  const {mutate} = useMutation<CreateRepositoryLabelMutationMutation, Error, CreateLabelInput, MutationContext>({
    mutationFn: async (input: CreateLabelInput) => {
      let response: Response
      try {
        response = await verifiedFetchJSON(`/${repositoryNameWithOwner}/labels/new`, {
          method: 'POST',
          body: {
            repositoryId,
            name: input.name,
            description: input.description,
            color: input.color,
          },
        })
      } catch {
        throw new Error(LABELS.labelCreateError)
      }

      if (!response.ok) {
        throw new Error(LABELS.labelCreateError)
      }

      const result: {data: CreateRepositoryLabelMutationMutation} = await response.json()
      const errors = result.data.createLabel?.errors

      if (errors && errors.length > 0) {
        throw new Error(formatFormErrorMessage(errors))
      }

      if (!result.data.createLabel?.label) {
        throw new Error(LABELS.labelCreateError)
      }

      return result.data
    },
    onMutate: async (input): Promise<MutationContext> => {
      const tempId = `${TEMP_LABEL_ID}${crypto.randomUUID()}`
      const previousData = queryKey ? queryClient.getQueryData<LabelsQueryData>(queryKey) : undefined

      // Optimistically close the dialog and insert the new label
      setDialogOpen(false)

      if (queryKey) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            labels.totalCount = (labels.totalCount ?? 0) + 1
            labels.edges.unshift({
              node: {
                id: tempId,
                name: input.name,
                nameHTML: sanitize(input.name, {
                  ALLOWED_TAGS: [],
                  ALLOWED_ATTR: [],
                  ALLOW_ARIA_ATTR: false,
                  RETURN_TRUSTED_TYPE: false,
                }),
                color: input.color,
                description: input.description,
              },
            })
          }),
        )
      }

      return {previousData, tempId}
    },
    onError: (error, _variables, context) => {
      if (queryKey && context?.previousData) {
        queryClient.setQueryData(queryKey, context.previousData)
      }
      setSubmissionErrors(error.message)
      setIsSubmittingRef.current(false)
      setDialogOpen(true)
    },
    onSuccess: (data, _variables, context) => {
      const createdLabel = data.createLabel?.label
      if (queryKey && createdLabel) {
        queryClient.setQueryData<LabelsQueryData>(
          queryKey,
          updateLabelsCache(labels => {
            const edge = labels.edges.find(e => e?.node?.id === context.tempId)
            if (edge?.node) {
              edge.node.id = createdLabel.id
              edge.node.name = createdLabel.name
              edge.node.nameHTML = createdLabel.nameHTML
              edge.node.color = createdLabel.color
              edge.node.description = createdLabel.description
            }
          }),
        )
      }
      onSuccess?.()
    },
  })

  return useCallback(
    (input: CreateLabelInput, setIsSubmitting: (isSubmitting: boolean) => void) => {
      setIsSubmittingRef.current = setIsSubmitting
      mutate(input)
    },
    [mutate],
  )
}
